import { z } from 'zod';

/**
 * Header note (relations-v2):
 *
 * Most exports in this file are part of the legacy `User.type` machinery
 * (`{ groups, roles }` triplet) that is being retired. New code should
 * NOT read or write these — use `User.roles[]` (`PartyRole[]`,
 * multi-valued, on `UserSchema`) and `Org.roles[]` instead.
 *
 * The unified signup service still STAMPS `User.type` for back-compat
 * via `__deprecatedDeriveLegacyUserType` until all consumers migrate.
 *
 * Migration guide and removal plan: `docs/relations-v2/08-user-identity.md`.
 */

/**
 * @deprecated Org-shape categorisation. Use `Org.roles[]` (`PartyRole[]`)
 * for new code. See `docs/relations-v2/08-user-identity.md`.
 */
// @TBC - freight_broker | carrier_company | business_customer | customs_clearance
export const mainOrgTypesSchema = z.enum(['business_customer', 'carrier_company', 'broker']);
/** @deprecated see {@link mainOrgTypesSchema}. */
export type MainOrgTypes = z.infer<typeof mainOrgTypesSchema>;

/**
 * @deprecated part of the legacy `User.type.groups[]` machinery. New code
 * uses `User.roles[]` / `Org.roles[]`. See
 * `docs/relations-v2/08-user-identity.md`.
 */
export const carrierGroupTypesSchema = z.enum(['driver', mainOrgTypesSchema.Enum.carrier_company]);
/** @deprecated see {@link carrierGroupTypesSchema}. */
export type CarrierGroupTypes = z.infer<typeof carrierGroupTypesSchema>;

/**
 * @deprecated part of the legacy `User.type.groups[]` machinery. New code
 * uses `User.roles[]` / `Org.roles[]`. See
 * `docs/relations-v2/08-user-identity.md`.
 */
export const customerGroupTypesSchema = z.enum([
  mainOrgTypesSchema.Enum.business_customer,
  'individual_customer',
]);
/** @deprecated see {@link customerGroupTypesSchema}. */
export type CustomerGroupTypes = z.infer<typeof customerGroupTypesSchema>;

/**
 * @deprecated part of the legacy `User.type.groups[]` machinery. New code
 * uses `User.roles[]` / `Org.roles[]`. See
 * `docs/relations-v2/08-user-identity.md`.
 */
export const brokerGroupTypesSchema = z.enum([mainOrgTypesSchema.Enum.broker]);
/** @deprecated see {@link brokerGroupTypesSchema}. */
export type BrokerGroupTypes = z.infer<typeof brokerGroupTypesSchema>;

/**
 * @deprecated part of the legacy `User.type.groups[]` machinery. Hero
 * staff are now tracked via the document `creator` snapshot — no PartyRole.
 */
export const adminUserGroupTypesSchema = z.enum(['super_admin']);
/** @deprecated see {@link adminUserGroupTypesSchema}. */
export type AdminUserGroupTypes = z.infer<typeof adminUserGroupTypesSchema>;

/**
 * @deprecated part of the legacy `User.type.groups[]` enum. Use
 * `User.roles[]` (`PartyRole[]`) instead. See
 * `docs/relations-v2/08-user-identity.md`.
 */
export const userGroupTypesSchema = z.enum([
  ...customerGroupTypesSchema.options,
  ...carrierGroupTypesSchema.options,
  ...brokerGroupTypesSchema.options,
  ...adminUserGroupTypesSchema.options,
  // api - third party apis
]);
/** @deprecated see {@link userGroupTypesSchema}. */
export type UserGroupTypes = z.infer<typeof userGroupTypesSchema>;
/** @deprecated see {@link userGroupTypesSchema}. */
export const userGroupsSchema = z.array(userGroupTypesSchema);
/** @deprecated see {@link userGroupTypesSchema}. */
export type UserGroups = z.infer<typeof userGroupsSchema>;

/**
 * @deprecated part of the legacy `User.type.roles[]` enum (NOT the new
 * `User.roles[]` / `partyRoleSchema`, which is the replacement). New
 * code should not branch on these values. See
 * `docs/relations-v2/08-user-identity.md`.
 */
export const userRoleTypesSchema = z.enum([
  'freelancer_driver',
  'carrier_company_driver',
  'business_customer_driver', // a driver that belongs to a business customer. For example, McDonald's has its own drivers
  'dispatcher',
  'employee',
  'admin',
  'individual_customer',
  'super_admin_system', // aka hero such as background jobs, automation.
  'super_admin_employee', // aka hero's employee
  'carrier_company_user', // a non driver user works for a carrier company
  'business_customer_user', // a non driver user works for a business customer
]);
/** @deprecated see {@link userRoleTypesSchema}. */
export const userRolesSchema = z.array(userRoleTypesSchema);
/** @deprecated see {@link userRoleTypesSchema}. */
export type UserRoleTypes = z.infer<typeof userRoleTypesSchema>;
/** @deprecated see {@link userRoleTypesSchema}. */
export type UserRoles = z.infer<typeof userRolesSchema>;

/**
 * @deprecated simplified categorisation derived from the legacy
 * `User.type` triplet. Use `User.roles[]` and role-set checks instead.
 * See `docs/relations-v2/08-user-identity.md`.
 */
export const userTypesSimpleSchema = z.enum([
  mainOrgTypesSchema.Enum.business_customer, // any business customer user
  'business_customer_user', // a user within business customer
  'individual_customer', // individual customer

  'super_admin_employee', // a super admin employee
  'super_admin_system', // aka hero such as background jobs, automation.

  mainOrgTypesSchema.Enum.carrier_company, // any carrier company user except drivers
  'carrier_company_user', // a user within carrier company ... non driver users
  'carrier_company_driver', // a driver works for a carrier company
  'freelancer_driver', // a driver works as a freelancer without a company

  mainOrgTypesSchema.Enum.broker, // a broker user (admin of a broker org)
]);
/** @deprecated see {@link userTypesSimpleSchema}. */
export type UserTypesSimple = z.infer<typeof userTypesSimpleSchema>;

//* type
/**
 * @deprecated the legacy `{ groups, roles }` triplet stored on
 * `User.type` and `User.orgs[i].userType`. New code uses
 * `User.roles[]` (`PartyRole[]`) for network identity and the
 * smaller member-role concept (admin / dispatcher / driver / employee)
 * for in-org permissions.
 *
 * The unified signup service still WRITES `User.type` for back-compat
 * via `__deprecatedDeriveLegacyUserType`. New feature code should NOT
 * read it. See `docs/relations-v2/08-user-identity.md`.
 */
export const userTypeSchema = z.object({
  /** the group that the user belongs to.
   *
   * For example the user can belong to:
   * - Carrier Group: this user can be driver dispatcher, or admin as carrier/service provider
   * - Business: the user
   *
   *
   * is the user belongs in the customer group?
   *
   * Or is the current user belongs to super admin groups (us/Hero)?
   *
   */
  groups: userGroupsSchema,
  /**
   * a role defines a set of actions the user can take within a specific group.
   *
   * For example, if the user belongs to group `business_customer` and has role `admin`,
   *
   * then this means that this user can anything in the business/organization account
   */
  roles: userRolesSchema,
});
/** @deprecated see {@link userTypeSchema}. */
export type UserType = z.infer<typeof userTypeSchema>;

/**
 * @deprecated member-role labels still in active use; will be replaced
 * by a smaller `memberRoleSchema` once `User.roles[]` becomes the
 * source of truth for network identity. See
 * `docs/relations-v2/08-user-identity.md`.
 */
export const userTypeUnifiedSchema = z.enum([
  'driver',
  'admin',
  'dispatcher',
  'employee',
  'non_driver_users',
]);
/** @deprecated see {@link userTypeUnifiedSchema}. */
export type UserTypeUnified = z.infer<typeof userTypeUnifiedSchema>;
